/**
 * @description Adiciona atributos a um elemento HTML com base nas opções fornecidas.
 *
 * @param {HTMLElement} elem - O elemento HTML ao qual os eventos serão adicionados.
 * @param {object} options - Um objeto contendo as opções do elemento.
 * @returns {HTMLElement} - O elemento HTML com os eventos adicionados.
 * 
 * @author Wellington Pereira
 * @since 08/08/2024
 * @version 2.0.0
 */
const allowUnsafeEval = String(
  (typeof process !== 'undefined' && process?.env?.ALLOW_UNSAFE_EVAL) || ''
).trim() === '1';
const looksLikeFunctionSource = (value = '') => {
  const normalized = String(value || '').trim();
  return normalized.startsWith('function(')
    || normalized.startsWith('function (')
    || normalized.startsWith('() =>')
    || normalized.startsWith('()=>')
    || normalized.startsWith('async () =>')
    || normalized.startsWith('async()=>');
};

export default (elem, options) => {
  let noAttributes = [
    'acceptedCards', 'active', 'aggregate', 'appearance', 'append', 'aria', 'blur',
    'callback', 'change', 'class', 'click', 'close', 'col', 'color', 'colors', 'cols',
    'colspan', 'content', 'data', 'dblclick', 'defaultvalue', 'dialog', 'drilldown',
    'element', 'fbClass', 'fbStyle', 'focus', 'full', 'forcedValue', 'groups',
    'hasFieldBox', 'hover', 'html', 'icon', 'ident', 'input', 'isGrid', 'keydown', 'keypress',
    'keyup', 'label', 'labels', 'labelPosition', 'legend', 'list', 'load', 'mask',
    'mode', 'mousedown', 'mousemove', 'mouseout', 'mouseover', 'mouseup', 'options',
    'ordered', 'parent', 'plotOptions', 'position', 'prefix', 'proto', 'quill',
    'rejectedCards', 'row', 'rows', 'rowspan', 'select', 'series', 'scale', 'scalename',
    'subtitle', 'suffix', 'style', 'sort', 'tag', 'text', 'theme', 'tooltip', 'unit',
    'unload', 'xAxis', 'yAxis', 'deferMinValidation', 'fieldIds', 'jsonContent',
    'jsonHeight', 'jsonForced', 'schema', 'schemaDefinitions', 'mainMenuBar', 'navigationBar',
    'statusBar', 'indentation', 'tabSize', 'askToFormat', 'invalidMessage', 'repairText',
    'repairErrorMessage', 'formatMessage', 'formatText', 'dismissFormatText',
    'maxchars', 'maxChars', 'maxlength', 'maxLength', 'characterCount', 'showCharacterCount',
    'visibleWhen', 'preserveSpace'
  ];

  if (!['INPUT', 'BUTTON'].includes(elem.tagName)) {
    noAttributes = [...noAttributes, 'max', 'min', 'step', 'type', 'value', 'micro'];
    if (options.element === 'chart') {
      noAttributes = [...noAttributes, 'title'];
    }
  } else {
    noAttributes = [...noAttributes, 'text', 'html', 'micro'];
  }

  for (const item in options) {
    const value = options[item];
    const hasAttributeValue = value !== undefined && value !== null && value !== false && value !== '';
    if (hasAttributeValue && !noAttributes.includes(item)) {
      if (typeof options[item] === 'function') {
        options[item] = options[item]();
      }

      if (typeof options[item] === 'string' && looksLikeFunctionSource(options[item])) {
        if (!allowUnsafeEval) {
          console.warn(`[security] Função em string bloqueada para atributo "${item}".`);
          continue;
        }
        try {
          const callable = Function(`"use strict"; return (${options[item]});`)();
          if (typeof callable === 'function') {
            options[item] = callable();
          } else {
            continue;
          }
        } catch {
          continue;
        }
      }

      elem.setAttribute(item, options[item]);

    }
  }

  if (options.aria !== undefined && typeof options.aria === 'object') {
    for (let item in options.aria) {
      elem.setAttribute(`aria-${item}`, options.aria[item]);
    }
  }

  if (options.data !== undefined && typeof options.data === 'object') {
    for (let key in options.data) {
      if (typeof elem.data === 'function') {
        elem.data(key, options.data[key]);
      } else if (elem.dataset) {
        elem.dataset[key] = options.data[key];
      } else {
        elem.setAttribute(`data-${key}`, options.data[key]);
      }
    }
  }

  if (options.proto !== undefined && typeof options.proto === 'object') {
    for (let item in options.proto) {
      if (options.proto.hasOwnProperty(item)) {
        elem[item] = options.proto[item];
      }
    }
  }

  if (options.class !== undefined && typeof options.class == 'string') {
    const classes = options.class.split(' ');
    for (const i in classes) {
      if (classes[i].length > 0) {
        elem.classList.add(classes[i]);
      }
    }
  }

  if (options.style !== undefined && (typeof options.style === 'string' || typeof options.style === 'object')) {
    if (typeof options.style === 'string') {
      const t1 = options.style.replace(/\s+/g, '').split(';');
      const style = {};
      for (const i in t1) {
        const t2 = t1[i].split(':');

        if (t2[0].length > 0) {
          style[t2[0]] = t2[1];
        }
      }

      options.style = style;
    }

    for (const key in options.style) {
      if (String(key).startsWith('--')) {
        elem.style.setProperty(key, options.style[key]);
      } else {
        elem.style[key] = options.style[key];
      }
    }
  }

  if (options.fbStyle !== undefined && (typeof options.fbStyle === 'string' || typeof options.fbStyle === 'object')) {
    if (typeof options.fbStyle === 'string') {
      const t1 = options.fbStyle.replace(/\s+/g, '').split(';');
      const style = {};
      for (const i in t1) {
        const t2 = t1[i].split(':');

        if (t2[0].length > 0) {
          style[t2[0]] = t2[1];
        }
      }

      options.fbStyle = style;
    }

    for (const key in options.fbStyle) {
      const parent = elem.parentElement || (typeof options.parent === 'string' ? document.querySelector(options.parent) : options.parent);
      if (String(key).startsWith('--')) {
        parent.style.setProperty(key, options.fbStyle[key]);
      } else {
        parent.style[key] = options.fbStyle[key];
      }
    }
  }

  if (options.row !== undefined && typeof options.row === 'number' && options.col !== undefined && typeof options.col === 'number') {
    const rowStart = (parseInt(options.row) + 1).toString();
    const rowEnd = (options.rowspan !== undefined) ? (`span ${options.rowspan}`) : ('auto');
    const colStart = (parseInt(options.col) + 1).toString();
    const colEnd = (options.colspan !== undefined) ? (`span ${options.colspan}`) : ('auto');
    elem.style.gridArea = `${rowStart} / ${colStart} / ${rowEnd} / ${colEnd}`;
  }

  const contentType = ['string', 'number'];

  if (options.text !== undefined && contentType.includes(typeof options.text) && options.text.length > 0 && elem.tagName !== 'INPUT') {
    elem.innerText = options.text;
  }

  if (options.html !== undefined && contentType.includes(typeof options.html) && options.html.length > 0 && elem.tagName !== 'INPUT') {
    elem.innerHTML = options.html;
  }

  return elem;
}
